import { userManager } from '@teko/auth';
import type { RefObject } from 'react';
import type { AssistantPanelRef } from 'teko-assistant-ui';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

interface ChatbotStore {
  open: boolean;
  unread: number;
  isEnabled: boolean;
  primaryColor?: string;
  sidebarWidth: number;
  chatRef: RefObject<AssistantPanelRef>;
  setOpen: (v: boolean) => void;
  setUnread: (v: number) => void;
  setIsEnabled: (v: boolean) => void;
  setSidebarConfig: (opts: {
    sidebarWidth: number;
    primaryColor?: string;
  }) => void;
}

const getUserSuffix = () => userManager.getInfo()?.userId ?? 'guest';

// Key per user so state doesn't bleed between accounts on the same browser tab
const chatbotStorage = createJSONStorage(() => ({
  getItem: (key: string) => sessionStorage.getItem(`${key}-${getUserSuffix()}`),
  setItem: (key: string, value: string) =>
    sessionStorage.setItem(`${key}-${getUserSuffix()}`, value),
  removeItem: (key: string) =>
    sessionStorage.removeItem(`${key}-${getUserSuffix()}`),
}));

export const useChatbotStore = create<ChatbotStore>()(
  persist(
    (set) => ({
      open: false,
      unread: 0,
      isEnabled: false,
      primaryColor: undefined,
      sidebarWidth: 400,
      chatRef: { current: null } as RefObject<AssistantPanelRef>,
      setOpen: (open) => set({ open }),
      setUnread: (unread) => set({ unread }),
      setIsEnabled: (isEnabled) => set({ isEnabled }),
      setSidebarConfig: ({ sidebarWidth, primaryColor }) =>
        set({ sidebarWidth, primaryColor }),
    }),
    {
      name: 'chatbot-state',
      storage: chatbotStorage,
      partialize: (state) => ({ open: state.open, unread: state.unread }),
    },
  ),
);
